import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Filter, RotateCcw, Check } from "lucide-react";
import AppButton from "./AppButton";
import IconButton from "./IconButton";
import { FormField } from "./FormField";
import { NativeSelect } from "./FormControls";
import cn from "./cn";

/**
 * FilterPanel — right-anchored slide-over filter drawer.
 *
 * The go-forward list-page filter pattern: inline search on the page, the rest
 * of the filters behind a Filters button, active choices shown as chips. Same
 * design on desktop and mobile, where it goes full-width.
 *
 * Fields go inside it as `FilterField` / `FilterSelectField`, laid out with
 * `FilterRow`.
 *
 * Props:
 *   show          — boolean visibility
 *   onHide        — close handler (header X, overlay, Escape). Alias: onClose
 *   title         — header title (default "Filters")
 *   subtitle      — header subtitle line
 *   children      — filter content
 *   onApply       — called by the Apply button, which then closes. Optional.
 *   onReset       — called by the Reset button. Optional.
 *   appliedCount  — number of active filters, shown in the footer
 *   applyLabel    — Apply button text (default "Apply Filters")
 *   resetLabel    — Reset button text (default "Reset Filters")
 *   width         — drawer width in px on desktop (default 480)
 *   closeOnOverlay — close on backdrop click (default true)
 *   closeOnEscape  — close on Escape (default true)
 */

const FilterPanel = ({
  show,
  onHide,
  onClose,
  title = "Filters",
  subtitle,
  children,
  onApply,
  onReset,
  appliedCount = 0,
  applyLabel = "Apply Filters",
  resetLabel = "Reset Filters",
  width = 480,
  closeOnOverlay = true,
  closeOnEscape = true,
}) => {
  const handleHide = onHide ?? onClose;

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape" && closeOnEscape) handleHide?.();
    },
    [handleHide, closeOnEscape]
  );

  useEffect(() => {
    if (!show) return undefined;
    document.addEventListener("keydown", handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [show, handleKeyDown]);

  if (!show) return null;

  const handleApply = () => {
    onApply?.();
    handleHide?.();
  };

  return createPortal(
    <div
      className="tw-fixed tw-inset-0 tw-bg-[var(--overlay-bg)] tw-backdrop-blur-[2px] tw-flex tw-justify-end tw-z-drawer tw-animate-panel-fade"
      onClick={closeOnOverlay ? handleHide : undefined}
      role="dialog"
      aria-modal="true"
    >
      <aside
        // The width is fed in as a custom property rather than an inline
        // `width`, so the mobile full-width utility can win without !important.
        // ⚠️ `tw-border-solid` on all three edges below. Preflight is off, so a
        // bare `tw-border-l` is a width and a colour on a border whose style is
        // still the initial `none` — it painted nothing, and the drawer edge,
        // the header rule and the footer rule have all been invisible.
        className="tw-relative tw-h-full tw-w-[var(--fp-w,480px)] tw-max-w-full max-[575.98px]:tw-w-full tw-bg-surface tw-text-content tw-border-0 tw-border-l tw-border-solid tw-border-line tw-shadow-[-18px_0_48px_hsl(var(--surface-dark)/0.22)] tw-flex tw-flex-col tw-animate-panel-slide-in"
        style={{ "--fp-w": `${width}px` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-px-[22px] tw-py-5 tw-border-0 tw-border-b tw-border-solid tw-border-line">
          <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-rounded tw-bg-primary/[0.12] tw-text-primary tw-shrink-0">
              <Filter size={18} />
            </span>
            <div className="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0">
              <span className="tw-text-lg tw-font-semibold tw-text-content tw-leading-[1.2]">
                {title}
              </span>
              {subtitle && (
                <span className="tw-text-[0.8125rem] tw-text-content-subtle tw-leading-[1.3]">
                  {subtitle}
                </span>
              )}
            </div>
          </div>
          <IconButton label="Close" icon={X} onClick={handleHide} className="tw-shrink-0" />
        </div>

        {/* Body */}
        <div className="tw-flex-auto tw-min-h-0 tw-overflow-y-auto tw-p-[22px] tw-flex tw-flex-col tw-gap-5">
          {children}
        </div>

        {/* Footer */}
        <div className="tw-border-0 tw-border-t tw-border-solid tw-border-line tw-px-[22px] tw-pt-3.5 tw-pb-4 tw-bg-surface-alt">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
            <AppButton variant="outline" onClick={onReset}>
              <RotateCcw size={15} />
              <span>{resetLabel}</span>
            </AppButton>
            <AppButton className="tw-flex-1 tw-justify-center" onClick={handleApply}>
              <Check size={16} />
              <span>{applyLabel}</span>
            </AppButton>
          </div>
          {appliedCount > 0 && (
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-mt-3 tw-text-[0.8125rem] tw-font-medium tw-text-primary">
              <Filter size={14} />
              <span>
                {appliedCount} {appliedCount === 1 ? "Filter" : "Filters"} Applied
              </span>
            </div>
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
};

/**
 * FilterField — one labelled filter inside the drawer.
 *
 * `FormField` with the drawer's muted label tone. It wires the label and the ids;
 * the control it wraps owns its own appearance, so pass `Input`, `NativeSelect`
 * or `Textarea` at `size="lg"`.
 */
const FilterField = ({ label, children, className = "" }) => (
  <FormField label={label} labelTone="muted" className={className}>
    {children}
  </FormField>
);

/**
 * FilterRow — lays out two FilterFields side by side, stacking below 480px.
 *
 * ⚠️ The breakpoint is deliberately 479.98px and not Tailwind's `sm` (640px).
 * They are 160px apart, so swapping one for the other is a layout change
 * disguised as a rename.
 */
const FilterRow = ({ children, className = "" }) => (
  <div className={cn("tw-grid tw-grid-cols-2 tw-gap-4 max-[479.98px]:tw-grid-cols-1", className)}>
    {children}
  </div>
);

/**
 * FilterSelectField — a labelled dropdown inside the drawer.
 *
 * `onChange` receives the VALUE, not the event — this is the convenience
 * wrapper, and `FilterField` + `NativeSelect` is the long form when a field
 * needs anything it does not cover.
 *
 * ⚠️ Not `SelectControl`, which is the standalone toolbar dropdown. This one is a
 * field inside the drawer.
 *
 * Props:
 *   label     — field label
 *   value     — current value
 *   onChange  — (nextValue) => void
 *   options   — [{ value, label }]
 *   className — extra classes on the field wrapper
 */
const FilterSelectField = ({ label, value, onChange, options = [], className = "" }) => (
  <FilterField label={label} className={className}>
    <NativeSelect
      size="lg"
      value={value}
      options={options}
      onChange={(e) => onChange?.(e.target.value)}
    />
  </FilterField>
);

export { FilterPanel, FilterField, FilterRow, FilterSelectField };
export default FilterPanel;
